import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { breadcrumbs, faqPage, qaPage, speakable, aeoGeo } from "@/lib/seo/schema";

const PATH = "/social-media-strategi-film";
const ANSWER =
  "En social media-strategi med film bygger på tre beslut: vilka kanaler ni prioriterar, vilka återkommande format ni publicerar och hur ofta. En fungerande start är en plan på 90 dagar med två till tre fasta filmformat, publicering varje vecka och uppföljning av tittartid och klick varannan vecka.";

const CHANNELS: [string, string, string, string][] = [
  ["LinkedIn", "30–90 sek", "1:1 eller 4:5", "Textning krävs, de flesta tittar utan ljud"],
  ["Instagram Reels", "15–45 sek", "9:16", "Hook inom 2 sekunder"],
  ["TikTok", "15–60 sek", "9:16", "Personligt tilltal, låg polering"],
  ["YouTube", "2–10 min", "16:9", "Sökbar titel och kapitel"],
  ["YouTube Shorts", "15–60 sek", "9:16", "Kan återanvända Reels"],
];

const FAQ: LandingFAQ[] = [
  { q: "Vad är en social media-strategi?", a: "En plan för vilka kanaler ni använder, vad ni publicerar, hur ofta, för vem och hur ni mäter om det fungerar." },
  { q: "Hur ofta ska man publicera film i sociala medier?", a: "Regelbundenhet slår volym. Minst en film per vecka per prioriterad kanal är en realistisk start för de flesta företag." },
  { q: "Hur lång ska en film på LinkedIn vara?", a: "30–90 sekunder fungerar bäst för de flesta budskap. Börja med budskapet direkt och texta alltid filmen." },
  { q: "Vilka mått ska man följa upp?", a: "Tittartid och andel som ser klart, klick till sajten och förfrågningar. Visningar ensamt säger lite om effekt." },
];

const TITLE = "Social media-strategi med film — plan för 90 dagar";
const DESC =
  "Så bygger ni en social media-strategi med film: kanalval, format per kanal (LinkedIn, Instagram, TikTok, YouTube), publiceringstakt och en plan för 90 dagar.";

export const Route = createFileRoute("/social-media-strategi-film")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: PATH, name: TITLE, description: DESC })) },
      { type: "application/ld+json", children: JSON.stringify(qaPage({ question: "Hur bygger man en social media-strategi med film?", answer: ANSWER, path: PATH, authorName: "Fenny" })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Film för sociala medier", path: "/sociala-medier-film" }, { name: "Social media-strategi", path: PATH }])) },
    ],
  }),
  component: Page,
});

const a = "underline decoration-mint/40 underline-offset-4 hover:text-mint";

function Page() {
  return (
    <>
      <LandingPage
        eyebrow="Guide — sociala medier"
        title={<>Social media-strategi med film: en plan för 90 dagar</>}
        lede={ANSWER}
        tldr={<p>Välj <strong>1–2 kanaler</strong>, <strong>2–3 fasta format</strong> och <strong>publicering varje vecka</strong>. Följ upp tittartid och klick varannan vecka och justera efter 90 dagar.</p>}
        ctaPrimary={{ label: "Se Pulse", to: "/pulse" }}
        ctaSecondary={{ label: "Film för sociala medier", to: "/sociala-medier-film" }}
        trackingId="social-media-strategi-film"
        sections={[
          {
            id: "plan-90-dagar",
            heading: "Planen: 90 dagar i tre steg",
            body: (
              <p>
                <strong>Dag 1–30: grund.</strong> Bestäm målgrupp, mål och 1–2 kanaler. Välj 2–3 återkommande
                format, till exempel kundcase, medarbetarröster och korta tips. Spela in material för en hel månad
                på en gång.{" "}
                <strong>Dag 31–60: rytm.</strong> Publicera varje vecka på fasta dagar. Testa två varianter av
                inledningen på samma film.{" "}
                <strong>Dag 61–90: förstärk.</strong> Gör mer av formaten som behåller tittarna och lägg ner det
                som inte gör det. Sätt nästa kvartals plan utifrån siffrorna.
              </p>
            ),
          },
          {
            id: "format-per-kanal",
            heading: "Format per kanal",
            body: (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-left text-sm">
                  <thead className="border-b border-border text-muted-foreground">
                    <tr><th className="py-2 pr-4 font-medium">Kanal</th><th className="py-2 pr-4 font-medium">Längd</th><th className="py-2 pr-4 font-medium">Bildformat</th><th className="py-2 font-medium">Viktigast</th></tr>
                  </thead>
                  <tbody>
                    {CHANNELS.map(([c, l, f, t]) => (
                      <tr key={c} className="border-b border-border/60"><td className="py-2 pr-4">{c}</td><td className="py-2 pr-4">{l}</td><td className="py-2 pr-4">{f}</td><td className="py-2 text-muted-foreground">{t}</td></tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-3 text-sm text-muted-foreground">Fler detaljer i <a className={a} href="/formatguide-2026">formatguiden 2026</a>.</p>
              </div>
            ),
          },
          {
            id: "lopande-produktion",
            heading: "Så håller ni takten",
            body: (
              <p>
                Det vanligaste skälet till att en strategi dör är att produktionen inte hinner med. Planera
                inspelning per månad i stället för per film, och överväg ett{" "}
                <a className={a} href="/pulse">filmabonnemang</a> där strategi, inspelning och versionering för
                alla kanaler ingår i ett fast månadspris. Läs också hur ni{" "}
                <a className={a} href="/mata-effekt-av-film">mäter effekten av film</a>.
              </p>
            ),
          },
        ]}
        faq={FAQ}
        closing={{
          title: "Vill ni ha en plan för era kanaler?",
          body: "Vi tar fram en filmplan för 90 dagar på strategimötet.",
          primary: { label: "Boka strategimöte", to: "/kontakt" },
        }}
      />
      <RelatedKnowledgeAuto topics={["sociala-medier", "process"]} />
    </>
  );
}
